// JSON-LD structured data builders (Schema.org).
//
// Each function returns a plain object that is serialized into a
// <script type="application/ld+json"> tag by the <Seo> / <JsonLd> components.
// Stable @id values let the graph cross-reference nodes (Organization,
// WebSite, Product, etc.) instead of duplicating them.

import {
  SITE_URL,
  PRODUCT_URL,
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  OG_IMAGE,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_ALT,
  LOGO,
  COMPANY_NAME,
  COMPANY_LEGAL_NAME,
  SUPPORT_EMAIL,
  CONTACT_EMAIL,
  COUNTRY,
  SAME_AS,
  absoluteUrl,
} from "./site";

// ---- Stable node identifiers -------------------------------------------------
export const ID = {
  organization: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
  software: `${SITE_URL}/#software`,
  product: `${SITE_URL}/#product`,
  brand: `${SITE_URL}/#brand`,
  logo: `${SITE_URL}/#logo`,
  ogImage: `${SITE_URL}/#primaryimage`,
  publisher: `${SITE_URL}/#organization`,
};

// ---- ImageObject -------------------------------------------------------------
export const logoImageObject = () => ({
  "@type": "ImageObject",
  "@id": ID.logo,
  url: LOGO,
  contentUrl: LOGO,
  width: 1080,
  height: 1080,
  caption: `${SITE_NAME} logo`,
});

export const ogImageObject = () => ({
  "@type": "ImageObject",
  "@id": ID.ogImage,
  url: OG_IMAGE,
  contentUrl: OG_IMAGE,
  width: OG_IMAGE_WIDTH,
  height: OG_IMAGE_HEIGHT,
  caption: OG_IMAGE_ALT,
});

// ---- Organization / Publisher ------------------------------------------------
export const organization = () => ({
  "@type": "Organization",
  "@id": ID.organization,
  name: COMPANY_NAME,
  legalName: COMPANY_LEGAL_NAME,
  url: SITE_URL,
  logo: logoImageObject(),
  image: { "@id": ID.ogImage },
  description: DEFAULT_DESCRIPTION,
  email: CONTACT_EMAIL,
  address: {
    "@type": "PostalAddress",
    addressCountry: COUNTRY,
  },
  sameAs: SAME_AS,
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: SUPPORT_EMAIL,
      availableLanguage: ["en", "id"],
    },
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: CONTACT_EMAIL,
      availableLanguage: ["en", "id"],
    },
  ],
});

// ---- Brand -------------------------------------------------------------------
export const brand = () => ({
  "@type": "Brand",
  "@id": ID.brand,
  name: SITE_NAME,
  logo: LOGO,
  url: SITE_URL,
});

// ---- WebSite -----------------------------------------------------------------
export const website = () => ({
  "@type": "WebSite",
  "@id": ID.website,
  url: SITE_URL,
  name: SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  inLanguage: ["en", "id"],
  publisher: { "@id": ID.organization },
});

// ---- Offers (from the pricing tiers) ----------------------------------------
// Marketing prices shown on the page; USD, per month.
const OFFERS = [
  { name: "Starter", price: "24", desc: "For solo operators — 500 leads/month, AI drafts, CRM." },
  { name: "Professional", price: "74", desc: "Unlimited leads, automation, approval workflow, priority support." },
  { name: "Business", price: "169", desc: "Multi-account, API access, SSO/SAML, audit logs." },
];

export const offers = () =>
  OFFERS.map((o) => ({
    "@type": "Offer",
    name: `${SITE_NAME} ${o.name}`,
    description: o.desc,
    price: o.price,
    priceCurrency: "USD",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: o.price,
      priceCurrency: "USD",
      unitText: "MONTH",
      billingIncrement: 1,
    },
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/#pricing`,
    category: "SaaS subscription",
  }));

const aggregateOffer = () => ({
  "@type": "AggregateOffer",
  priceCurrency: "USD",
  lowPrice: "24",
  highPrice: "199",
  offerCount: 4,
  offers: offers(),
  url: `${SITE_URL}/#pricing`,
});

// ---- SoftwareApplication -----------------------------------------------------
export const softwareApplication = () => ({
  "@type": "SoftwareApplication",
  "@id": ID.software,
  name: SITE_NAME,
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "CRM, Social Lead Generation, Marketing Automation",
  operatingSystem: "Web",
  url: PRODUCT_URL,
  description: DEFAULT_DESCRIPTION,
  image: { "@id": ID.ogImage },
  screenshot: OG_IMAGE,
  softwareHelp: `${SITE_URL}/contact`,
  featureList: [
    "AI social lead discovery",
    "Real-time buying-intent scoring",
    "AI-drafted replies with approval workflow",
    "Multi-channel engagement scheduling",
    "Sales pipeline & CRM",
    "Team roles, review queues and audit logs",
    "Lead export & CRM sync (HubSpot, Salesforce, Pipedrive)",
  ],
  offers: aggregateOffer(),
  publisher: { "@id": ID.organization },
  brand: { "@id": ID.brand },
});

// ---- Product -----------------------------------------------------------------
export const product = () => ({
  "@type": "Product",
  "@id": ID.product,
  name: SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  image: { "@id": ID.ogImage },
  brand: { "@id": ID.brand },
  url: PRODUCT_URL,
  category: "Sales & Marketing Software",
  offers: aggregateOffer(),
});

// ---- FAQPage -----------------------------------------------------------------
export const faqPage = (faqs = []) => ({
  "@type": "FAQPage",
  "@id": `${SITE_URL}/#faq`,
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
});

// ---- BreadcrumbList ----------------------------------------------------------
export const breadcrumb = (items = []) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: absoluteUrl(it.path),
  })),
});

// ---- WebPage (+ typed variants) ----------------------------------------------
export const webPage = ({
  path = "/",
  name,
  description,
  type = "WebPage",
  breadcrumbItems,
  primaryImage = true,
} = {}) => {
  const url = absoluteUrl(path);
  const node = {
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { "@id": ID.website },
    about: { "@id": ID.organization },
    inLanguage: "en",
  };
  if (primaryImage) {
    node.primaryImageOfPage = { "@id": ID.ogImage };
    node.image = { "@id": ID.ogImage };
  }
  if (breadcrumbItems && breadcrumbItems.length) {
    node.breadcrumb = breadcrumb(breadcrumbItems);
  }
  return node;
};

export const contactPage = (opts = {}) =>
  webPage({ ...opts, type: "ContactPage" });

export const privacyPage = (opts = {}) =>
  webPage({ ...opts, type: "PrivacyPolicy" });

export const termsPage = (opts = {}) =>
  webPage({ ...opts, type: "TermsOfService" });

// ---- Graph assembler ---------------------------------------------------------
// Wraps any set of nodes into a single @graph document. Shared foundational
// nodes (org, website, brand, images) are always included so every page is
// self-describing for crawlers that fetch one URL in isolation.
export const graph = (nodes = []) => ({
  "@context": "https://schema.org",
  "@graph": [
    organization(),
    website(),
    brand(),
    ogImageObject(),
    logoImageObject(),
    ...nodes,
  ],
});
